import * as InFlowEnums from "ipctg-inflow-types/InFlowEnums"

import { startCase } from "./etc"
import { TranslationKey } from "./types/local"

export class ExpectedError extends Error {
  constructor(
    failReason: string,
    translationKey: TranslationKey,
    name: string
  ) {
    super(translationKey)
    this.translationKey = translationKey
    this.name = name
    this.failReason = failReason
    Object.setPrototypeOf(this, ExpectedError.prototype)
  }
  failReason: string
  translationKey: TranslationKey
}

export class GatedAccountError extends ExpectedError {
  constructor(failReason: InFlowEnums.AccountFailReason) {
    const key = toLowerFirstKey(InFlowEnums.AccountFailReason, failReason)
    super(failReason, `errors.account.${key}`, startCase(failReason))
  }
}

export class GatedAppUserError extends ExpectedError {
  constructor(failReason: InFlowEnums.AppUserPUTFailReason) {
    const key = toLowerFirstKey(InFlowEnums.AppUserPUTFailReason, failReason)
    super(failReason, `errors.appUser.${key}`, startCase(failReason))
  }
}

export class GatedAutoPaySelectCardError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedAutoPaySelectCardFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedAutoPaySelectCardFailReason,
      failReason
    )
    super(failReason, `errors.autoPaySelectCard.${key}`, startCase(failReason))
  }
}

export class GatedWelcomeError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedWelcomeFailReason) {
    const key = toLowerFirstKey(InFlowEnums.GatedWelcomeFailReason, failReason)
    super(failReason, `errors.welcome.${key}`, startCase(failReason))
  }
}

export class GatedCheckoutError extends ExpectedError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    const key = toLowerFirstKey(InFlowEnums.CheckoutFailReason, failReason)
    super(failReason, `errors.gatedCheckoutError.${key}`, startCase(failReason))
  }
}

export class GatedConfirmSetupIntentError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedConfirmSetupIntentFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedConfirmSetupIntentFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedConfirmSetupIntentError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedConfirmSetupIntentWalletError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedConfirmSetupIntentWalletFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedConfirmSetupIntentWalletFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedConfirmSetupIntentWalletError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedEmailConfirmationError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedEmailConfirmationFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedEmailConfirmationFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedEmailConfirmationError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedEmailReceiptError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedEmailReceiptFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedEmailReceiptFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedEmailReceiptError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedGetConfirmationDetailsError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedGetConfirmationDetailsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedGetConfirmationDetailsFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedGetConfirmationDetailsError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedLookupSessionsError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedLookupSessionsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedLookupSessionsFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedLookupSessionsError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedRegisterSessionError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedRegisterSessionFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedRegisterSessionFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedRegisterSessionError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedPayError extends GatedCheckoutError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    super(failReason)
  }
}

export class GatedPaymentIntentError extends GatedCheckoutError {
  constructor(failReason: InFlowEnums.CheckoutFailReason) {
    super(failReason)
  }
}

export class GatedPromoPaymentError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedPromoPaymentFailReasonType) {
    const key = (failReason![0].toLowerCase() +
      failReason!.slice(
        1
      )) as PascalToLowerFirst<InFlowEnums.GatedPromoPaymentFailReasonType>
    super(
      failReason,
      `errors.gatedPromoPaymentError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedPromoRegisterError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedPromoRegisterFailReasonType) {
    const key = (failReason![0].toLowerCase() +
      failReason!.slice(
        1
      )) as PascalToLowerFirst<InFlowEnums.GatedPromoRegisterFailReasonType>
    super(
      failReason,
      `errors.gatedPromoRegisterError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedReceiptError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedReceiptFailReason) {
    const key = toLowerFirstKey(InFlowEnums.GatedReceiptFailReason, failReason)
    super(failReason, `errors.gatedReceiptError.${key}`, startCase(failReason))
  }
}

export class GatedSavedPaymentMethodActionError extends ExpectedError {
  constructor(
    failReason: InFlowEnums.GatedSavedPaymentMethodActionsFailReason
  ) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedSavedPaymentMethodActionsFailReason,
      failReason
    )
    super(
      failReason,
      `errors.savedPaymentMethodActionError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedSavedPaymentMethodsError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedSavedPaymentMethodsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedSavedPaymentMethodsFailReason,
      failReason
    )
    super(
      failReason,
      `errors.savedPaymentMethodsError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedSessionDetailsError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedSessionDetailsFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedSessionDetailsFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedSessionDetailsError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedSetupIntentError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedSetupIntentFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedSetupIntentFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedSetupIntentError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedSetupIntentWalletError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedSetupIntentWalletFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedSetupIntentWalletFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedSetupIntentWalletError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedTagCashError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedTagCashFailReason) {
    const key = toLowerFirstKey(InFlowEnums.GatedTagCashFailReason, failReason)
    super(failReason, `errors.gatedTagCashError.${key}`, startCase(failReason))
  }
}

export class GatedTextConfirmationError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedTextConfirmationFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedTextConfirmationFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedTextConfirmationError.${key}`,
      startCase(failReason)
    )
  }
}

export class GatedTextReceiptError extends ExpectedError {
  constructor(failReason: InFlowEnums.GatedTextReceiptFailReason) {
    const key = toLowerFirstKey(
      InFlowEnums.GatedTextReceiptFailReason,
      failReason
    )
    super(
      failReason,
      `errors.gatedTextReceiptError.${key}`,
      startCase(failReason)
    )
  }
}

type PascalToLowerFirst<S extends string> =
  S extends `${infer First}${infer Rest}` ? `${Lowercase<First>}${Rest}` : S

// Type to get the key name from an enum value
type ValueToKey<T extends Record<string, string>, V extends T[keyof T]> = {
  [K in keyof T]: T[K] extends V ? K : never
}[keyof T]

// Type to convert enum value to lowerFirst key name
type EnumValueToLowerFirstKey<
  T extends Record<string, string>,
  V extends T[keyof T],
> = PascalToLowerFirst<
  ValueToKey<T, V> extends string ? ValueToKey<T, V> : never
>

const toLowerFirstKey = <
  T extends Record<string, string>,
  V extends T[keyof T],
>(
  enumObj: T,
  value: V
): EnumValueToLowerFirstKey<T, V> => {
  const key = Object.keys(enumObj).find((k) => enumObj[k] === value)
  return (key![0].toLowerCase() + key!.slice(1)) as EnumValueToLowerFirstKey<
    T,
    V
  >
}
